import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Descriptions,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  App,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { axios } from 'lib/axios';
import PageShell, { useBrandToken } from 'components/PageShell';
import { OutletDetailBody, categoryLabel } from 'lib/adminDetail';

const OutletsPage = () => {
  const { message } = App.useApp();
  const colors = useBrandToken();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const fetchOutlets = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/outlet', { params: { limit: 100 } });
      const list = res?.data || [];
      setRows(Array.isArray(list) ? list : []);
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOutlets();
  }, []);

  const filtered = useMemo(() => {
    let list = rows;
    if (statusFilter === 'verified') {
      list = list.filter((o) => o.is_verified);
    } else if (statusFilter === 'pending') {
      list = list.filter((o) => !o.is_verified);
    }
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((o) =>
      `${o.name || ''} ${o.city || ''} ${o.user?.full_name || ''} ${o.address || ''}`
        .toLowerCase()
        .includes(q),
    );
  }, [rows, search, statusFilter]);

  const showDetail = async (record) => {
    try {
      const res = await axios.get(`/admin/outlet/${record._id}`);
      setDetail(res?.data || null);
      setDetailOpen(true);
    } catch (e) {
      message.error('Could not load outlet details');
    }
  };

  const columns = [
    {
      title: 'Outlet',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <Space direction="vertical" size={0}>
          <strong>{name}</strong>
          <span style={{ color: colors.muted, fontSize: 12 }}>
            {record.address || record.city || ''}
          </span>
        </Space>
      ),
    },
    {
      title: 'Owner',
      key: 'user',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <span>{r.user?.full_name || '—'}</span>
          <span style={{ color: colors.muted, fontSize: 12 }}>
            {r.user?.email || ''}
          </span>
        </Space>
      ),
    },
    {
      title: 'Category',
      key: 'category',
      render: (_, r) => categoryLabel(r.category),
    },
    {
      title: 'City',
      dataIndex: 'city',
      key: 'city',
      render: (v) => v || '—',
    },
    {
      title: 'Mobile',
      key: 'mobile',
      render: (_, r) => r.mobile_number || r.user?.mobile_number || '—',
    },
    {
      title: 'Hours',
      key: 'hours',
      render: (_, r) =>
        r.opening_time
          ? `${r.opening_time} – ${r.closing_time || '—'}`
          : '—',
    },
    {
      title: 'Status',
      dataIndex: 'is_verified',
      key: 'is_verified',
      render: (v) =>
        v ? <Tag color="green">Verified</Tag> : <Tag color="orange">Pending</Tag>,
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            showDetail(record);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <PageShell
      title="Outlets"
      description="Every store with owner, category, hours, license, photos, and documents."
      actions={
        <Button icon={<ReloadOutlined />} onClick={fetchOutlets} loading={loading}>
          Refresh
        </Button>
      }
    >
      <Card style={{ borderRadius: 12, border: `1px solid ${colors.border}` }}>
        <Space wrap style={{ marginBottom: 16 }}>
          <Input.Search
            placeholder="Search outlet, city, owner"
            allowClear
            style={{ width: 320 }}
            onSearch={setSearch}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 160 }}
            options={[
              { value: 'all', label: 'All statuses' },
              { value: 'verified', label: 'Verified' },
              { value: 'pending', label: 'Pending' },
            ]}
          />
        </Space>
        <Table
          rowKey={(r) => r._id}
          loading={loading}
          columns={columns}
          dataSource={filtered}
          pagination={{ pageSize: 10, showSizeChanger: true }}
          onRow={(record) => ({
            onClick: () => showDetail(record),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      <Modal
        title={detail?.outlet?.name || 'Outlet details'}
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={[
          <Button key="close" onClick={() => setDetailOpen(false)}>
            Close
          </Button>,
        ]}
        width={760}
      >
        <OutletDetailBody
          outlet={detail?.outlet}
          extra={
            <>
              <Descriptions.Item label="Coupons on this store">
                {(detail?.outletCoupons || []).length}
              </Descriptions.Item>
              <Descriptions.Item label="Happy hours">
                {detail?.happyHours ? 'Yes' : 'No'}
              </Descriptions.Item>
              <Descriptions.Item label="Hot offer" span={2}>
                {detail?.hotOffer ? 'Yes' : 'No'}
              </Descriptions.Item>
              <Descriptions.Item label="Coupon titles" span={2}>
                {(detail?.outletCoupons || [])
                  .map((c) => c.name || c.title)
                  .filter(Boolean)
                  .join(', ') || '—'}
              </Descriptions.Item>
            </>
          }
        />
      </Modal>
    </PageShell>
  );
};

export default OutletsPage;
